import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, User } from "lucide-react";

export default function LoginPage() {
    const { login } = useAuth();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || "/dashboard";

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        const res = await login(username.trim(), password);
        setSubmitting(false);
        if (!res.ok) {
            setError(res.error || "Login failed");
            return;
        }
        if (res.user.must_change_password) {
            navigate("/change-password", { replace: true });
        } else {
            navigate(from, { replace: true });
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-6">
            <div className="w-full max-w-md">
                <div className="mb-10 text-center">
                    <div className="inline-flex h-12 w-12 rounded-2xl bg-foreground text-background items-center justify-center font-display font-bold text-xl mb-5">
                        A
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold">
                        Welcome back
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        Sign in to your AppDash control panel
                    </p>
                </div>

                <form
                    onSubmit={submit}
                    className="glass-tile rounded-3xl p-8 space-y-5"
                    data-testid="login-form"
                >
                    <div className="space-y-1.5">
                        <Label htmlFor="username">Username</Label>
                        <div className="relative">
                            <User className="h-4 w-4 absolute top-3 left-3 text-muted-foreground" />
                            <Input
                                id="username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="admin"
                                className="pl-9 h-11 rounded-xl"
                                data-testid="input-username"
                                autoComplete="username"
                                required
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="password">Password</Label>
                        <div className="relative">
                            <Lock className="h-4 w-4 absolute top-3 left-3 text-muted-foreground" />
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="pl-9 h-11 rounded-xl"
                                data-testid="input-password"
                                autoComplete="current-password"
                                required
                            />
                        </div>
                    </div>

                    {error && (
                        <div
                            className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2.5"
                            data-testid="login-error"
                        >
                            {error}
                        </div>
                    )}

                    <Button
                        type="submit"
                        className="w-full h-11 rounded-xl text-sm font-semibold"
                        disabled={submitting}
                        data-testid="login-submit"
                    >
                        {submitting ? "Signing in..." : "Sign in"}
                    </Button>

                    <p className="text-xs text-center text-muted-foreground">
                        Default admin: <span className="font-mono">admin / Welcome123</span>
                    </p>
                </form>
            </div>
        </div>
    );
}
